import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import {
  CatalogApi,
  CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum,
  CatalogControllerGetPaginatedServiceCatalogSortEnum,
} from "@/config/apiClient";
import { type SortOption } from "@/config/apiClient.types";
import { createApiClient } from "@/config/createApiClient";
import {
  type AvailablePageLength,
  DEFAULT_PAGE_LENGTH,
} from "@/constants/catalogMediumDisplayed";
import { currentWorkspaceAtom } from "@/store";

type CatalogSortEnumValue =
  (typeof CatalogControllerGetPaginatedServiceCatalogSortEnum)[keyof typeof CatalogControllerGetPaginatedServiceCatalogSortEnum];
type CatalogNbPerPageEnumValue =
  (typeof CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum)[keyof typeof CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum];

type QueryGetCatalog = {
  currentPage: number;
  sort: SortOption;
  search?: string;
  nbPerPage?: AvailablePageLength;
  categoriesIds?: Array<string>;
};

const apiClient = createApiClient(CatalogApi);

export const useCatalog = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const useGetCatalog = ({
    currentPage,
    sort,
    search = "",
    nbPerPage = DEFAULT_PAGE_LENGTH,
    categoriesIds,
  }: QueryGetCatalog) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "catalog",
        currentPage,
        sort,
        search,
        nbPerPage,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
        categoriesIds,
      ],
      enabled: !!currentWorkspace,
      queryFn: () => {
        type QueryParams = Omit<
          Parameters<
            typeof apiClient.catalogControllerGetPaginatedServiceCatalog
          >["0"],
          "clientServiceId"
        >;

        const sortMap: Record<SortOption, CatalogSortEnumValue> = {
          default: CatalogControllerGetPaginatedServiceCatalogSortEnum.Default,
          alphabeticalASC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalAsc,
          alphabeticalDESC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalDesc,
        };

        const nbPerPageMap: Record<
          AvailablePageLength,
          CatalogNbPerPageEnumValue
        > = {
          10: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_10,
          20: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_20,
          50: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_50,
          100: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_100,
        };

        const queryParams: QueryParams = {
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          page: String(currentPage),
          sort: sortMap[sort],
          search,
          nbPerPage: nbPerPageMap[nbPerPage],
          categoriesIds: categoriesIds?.join(";"),
        };

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) =>
              typeof value === "number" ||
              (typeof value === "string" && value.trim() !== "") ||
              (Array.isArray(value) && value.length > 0)
          )
        );

        return apiClient.catalogControllerGetPaginatedServiceCatalog({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          ...cleanQueryParams,
        });
      },
    });

  const useGetCatalogInfinite = ({
    sort,
    search = "",
    nbPerPage = DEFAULT_PAGE_LENGTH,
    categoriesIds,
  }: Omit<QueryGetCatalog, "currentPage">) => {
    type CatalogResponse = Awaited<
      ReturnType<typeof apiClient.catalogControllerGetPaginatedServiceCatalog>
    >;

    return useInfiniteQuery<CatalogResponse>({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "catalogInfinite",
        sort,
        search,
        nbPerPage,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
        categoriesIds,
      ],
      enabled: !!currentWorkspace,
      initialPageParam: 1,
      queryFn: ({ pageParam }) => {
        const currentPage = typeof pageParam === "number" ? pageParam : 1;

        const sortMap: Record<SortOption, CatalogSortEnumValue> = {
          default: CatalogControllerGetPaginatedServiceCatalogSortEnum.Default,
          alphabeticalASC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalAsc,
          alphabeticalDESC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalDesc,
        };

        const nbPerPageMap: Record<
          AvailablePageLength,
          CatalogNbPerPageEnumValue
        > = {
          10: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_10,
          20: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_20,
          50: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_50,
          100: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_100,
        };

        const queryParams = {
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          page: String(currentPage - 1),
          sort: sortMap[sort],
          search,
          nbPerPage: nbPerPageMap[nbPerPage],
          categoriesIds: categoriesIds?.join(";"),
        };

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) =>
              typeof value === "number" ||
              (typeof value === "string" && value.trim() !== "") ||
              (Array.isArray(value) && value.length > 0)
          )
        );

        return apiClient.catalogControllerGetPaginatedServiceCatalog({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          ...cleanQueryParams,
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        const nbPages = lastPage?.data?.nbPages ?? 0;
        const currentPage = allPages.length;
        return currentPage < nbPages ? currentPage + 1 : undefined;
      },
    });
  };

  const useGetCatalogMedium = (id: string) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "catalogMedium",
        id,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      queryFn: () =>
        apiClient.catalogControllerGetCatalogMedium({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          catalogMediumId: id,
        }),
      enabled: !!currentWorkspace,
    });

  const useCreateOrUpdateEcoScore = () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["createOrUpdateEcoScore"],
      mutationFn: ({
        catalogMediumId,
        updateEcoScoreCommand,
      }: {
        catalogMediumId: string;
        updateEcoScoreCommand: Parameters<
          typeof apiClient.catalogControllerUpdateCatalogMediumEcoScore
        >[0]["updateEcoScoreCommand"];
      }) =>
        apiClient.catalogControllerUpdateCatalogMediumEcoScore({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          catalogMediumId,
          updateEcoScoreCommand,
        }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalog"] }),
    });
  };

  return {
    useGetCatalog,
    useGetCatalogInfinite,
    useGetCatalogMedium,
    useCreateOrUpdateEcoScore,
  };
};
